import { Locale, format as formatWithDateFns } from 'date-fns';
import { cs, de, el, enGB, es, fr, pl, ru } from 'date-fns/locale';
import i18next from 'i18next';

import { AppLanguage } from '../../Shared/Enums';

export const NBSP = '\u00A0';

export type DateStyle = 'short' | 'shortWithTime' | 'slashed' | 'long';

type DateStyles = Record<DateStyle, string>;

const defaultDateFormats: DateStyles = {
	short: 'DD.MM.YYYY',
	shortWithTime: 'DD.MM.YYYY HH:mm',
	slashed: 'DD/MM/YYYY',
	long: 'D MMMM YYYY',
};

const czechDateFormats: DateStyles = {
	short: 'D. M. YYYY',
	shortWithTime: 'D. M. YYYY H:mm',
	slashed: 'D. M. YYYY',
	long: 'D. MMMM YYYY',
};

// English, Spanish and Greek separate with a slash, where a dot would read as an American
// month-first date. Day-first ordering stays everywhere - the customer base is European.
const slashedDateFormats: DateStyles = {
	short: 'DD/MM/YYYY',
	shortWithTime: 'DD/MM/YYYY HH:mm',
	slashed: 'DD/MM/YYYY',
	long: 'D MMMM YYYY',
};

const dateFormatsByLanguage: Record<string, DateStyles> = {
	[AppLanguage.Czech]: czechDateFormats,
	[AppLanguage.English]: slashedDateFormats,
	[AppLanguage.Spanish]: slashedDateFormats,
	[AppLanguage.Greek]: slashedDateFormats,
};

const datePlaceholdersByLanguage: Record<string, string> = {
	[AppLanguage.Czech]: `--.${NBSP}--.${NBSP}----`,
};

const datePlaceholdersBySeparator: Record<string, string> = {
	[AppLanguage.English]: '--/--/----',
	[AppLanguage.Spanish]: '--/--/----',
	[AppLanguage.Greek]: '--/--/----',
};

const dateInputHintsByLanguage: Record<string, string> = {
	[AppLanguage.Czech]: 'd. m. rrrr',
	[AppLanguage.English]: 'dd/mm/yyyy',
	[AppLanguage.Spanish]: 'dd/mm/aaaa',
	[AppLanguage.Greek]: 'ηη/μμ/εεεε',
};

const dateFnsLocales: Record<string, Locale> = {
	[AppLanguage.Polish]: pl,
	[AppLanguage.English]: enGB,
	[AppLanguage.Czech]: cs,
	[AppLanguage.German]: de,
	[AppLanguage.Spanish]: es,
	[AppLanguage.Russian]: ru,
	[AppLanguage.French]: fr,
	[AppLanguage.Greek]: el,
};

// Mirrors the cultures the backend uses for documents and mailings,
// so a price rendered in the panel matches the one on the invoice.
const localeTags: Record<string, string> = {
	[AppLanguage.Polish]: 'pl-PL',
	[AppLanguage.English]: 'en-GB',
	[AppLanguage.Czech]: 'cs-CZ',
	[AppLanguage.German]: 'de-AT',
	[AppLanguage.Spanish]: 'es-ES',
	[AppLanguage.Russian]: 'ru-RU',
	[AppLanguage.French]: 'fr-FR',
	[AppLanguage.Greek]: 'el-GR',
};

const FALLBACK_LOCALE_TAG = 'en-GB';

const czech: string = AppLanguage.Czech;

export const getAppLanguage = (): string => {
	const language = i18next?.language || AppLanguage.English;
	return language.includes('-') ? language.split('-')[0] : language;
};

export const isCzechLanguage = (language: string = getAppLanguage()) => language === czech;

export const getLocaleTag = (language: string = getAppLanguage()): string =>
	localeTags[language] ?? FALLBACK_LOCALE_TAG;

export const getDateFnsLocale = (language: string = getAppLanguage()): Locale => dateFnsLocales[language] ?? enGB;

export const getDateFnsLocales = (): Record<string, Locale> => dateFnsLocales;

export const getDateFormat = (style: DateStyle = 'short', language: string = getAppLanguage()): string =>
	dateFormatsByLanguage[language]?.[style] ?? defaultDateFormats[style];

export const toDateFnsPattern = (momentPattern: string): string => momentPattern.replace(/D/g, 'd').replace(/Y/g, 'y');

export const getDateFnsFormat = (style: DateStyle = 'short', language: string = getAppLanguage()): string =>
	toDateFnsPattern(getDateFormat(style, language));

export const getDatePlaceholder = (language: string = getAppLanguage()): string =>
	datePlaceholdersByLanguage[language] ?? datePlaceholdersBySeparator[language] ?? '--.--.----';

export const getDateInputHint = (language: string = getAppLanguage()): string =>
	dateInputHintsByLanguage[language] ?? 'dd.mm.rrrr';

export const formatLongDate = (date: Date | string | number): string =>
	formatWithDateFns(new Date(date), getDateFnsFormat('long'), { locale: getDateFnsLocale() });

export const formatShortDate = (date?: Date | string | number | null): string =>
	date ? formatWithDateFns(new Date(date), getDateFnsFormat('short'), { locale: getDateFnsLocale() }) : '';

interface Separators {
	decimal: string;
	group: string;
}

const separatorsCache = new Map<string, Separators>();

// Separators come from the language's own number format — a comma in Polish
// and Czech, a dot in English — instead of being assumed.
const getSeparators = (language: string = getAppLanguage()): Separators => {
	const cached = separatorsCache.get(language);
	if (cached) return cached;

	const parts = new Intl.NumberFormat(getLocaleTag(language), { minimumFractionDigits: 2 }).formatToParts(1234.5);
	const separators: Separators = {
		decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
		group: parts.find((part) => part.type === 'group')?.value ?? NBSP,
	};

	separatorsCache.set(language, separators);
	return separators;
};

export const getDecimalSeparator = (language: string = getAppLanguage()): string => getSeparators(language).decimal;

export const getGroupSeparator = (language: string = getAppLanguage()): string => getSeparators(language).group;

export const formatDecimal = (value: number, fractionDigits: number = 2, language: string = getAppLanguage()): string =>
	new Intl.NumberFormat(getLocaleTag(language), {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(value);

const fractionDigitsOfNumber = (value: number): number => value.toString().split('.')[1]?.length ?? 0;

const fractionDigitsOfText = (value: string, decimal: string): number => {
	const index = value.lastIndexOf(decimal);
	return index === -1 ? 0 : value.length - index - 1;
};

const parseLocalized = (value: string, separators: Separators): number | null => {
	const withoutGroups = value.trim().split(separators.group).join('').replace(/\s/g, '');
	const normalized = separators.decimal === '.' ? withoutGroups : withoutGroups.split(separators.decimal).join('.');
	const parsed = Number(normalized);

	return Number.isFinite(parsed) ? parsed : null;
};

// Accepts a raw number or a string this module already formatted, so wrapping
// an already formatted price does not change it.
export const groupThousands = (value: number | string, language: string = getAppLanguage()): string => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? formatDecimal(value, fractionDigitsOfNumber(value), language) : '';
	}

	const separators = getSeparators(language);
	const parsed = parseLocalized(value, separators);

	return parsed === null ? value : formatDecimal(parsed, fractionDigitsOfText(value, separators.decimal), language);
};

export const withCurrency = (formattedValue: string, currencyLabel?: string): string =>
	currencyLabel ? `${formattedValue}${NBSP}${currencyLabel}` : formattedValue;
